import React, { useState, useEffect } from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';

const DEFECT_CATEGORIES = [
    "Wrong PKD",
    "Wrong Expiry",
    "Smudge Print",
    "Missing Print"
];

const CodeVerification: React.FC = () => {
    const [metadata, setMetadata] = useState({
        productName: '',
        sku: '',
        batchNo: '',
        pkd: '',
        expiry: ''
    });

    const { dispatch } = usePackagingOperationsContext();

    const [samples, setSamples] = useState(
        Array.from({ length: 10 }).map((_, i) => ({
            id: i,
            status: 'Okay',
            defectCategory: '',
            defectCount: '',
            file: null as File | null
        }))
    );

    const handleSampleChange = (index: number, field: string, value: any) => {
        setSamples(prev => {
            const newSamples = [...prev];
            newSamples[index] = { ...newSamples[index], [field]: value };
            
            if (field === 'status' && value === 'Okay') {
                newSamples[index].defectCategory = '';
                newSamples[index].defectCount = '';
                newSamples[index].file = null;
            }
            
            return newSamples;
        });
    };

    useEffect(() => {
        dispatch({
            type: 'SET_CHECKLIST_DATA',
            payload: { metadata, samples }
        });
    }, [metadata, samples, dispatch]);

    return (
        <div className="p-4 space-y-6">
            <h3 className="text-lg font-bold text-gray-800 border-b pb-2">Code Verification Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Product Name</label>
                    <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border" 
                        value={metadata.productName} onChange={e => setMetadata({...metadata, productName: e.target.value})} placeholder="Enter Product Name" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">SKU</label>
                    <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border" 
                        value={metadata.sku} onChange={e => setMetadata({...metadata, sku: e.target.value})} placeholder="Enter SKU" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Batch No</label>
                    <input type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border" 
                        value={metadata.batchNo} onChange={e => setMetadata({...metadata, batchNo: e.target.value})} placeholder="Enter Batch No" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">PKD</label>
                    <input type="date" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border" 
                        value={metadata.pkd} onChange={e => setMetadata({...metadata, pkd: e.target.value})} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                    <input type="date" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border" 
                        value={metadata.expiry} onChange={e => setMetadata({...metadata, expiry: e.target.value})} />
                </div>
            </div>

            <div>
                <h4 className="text-md font-bold text-gray-800 mb-2">Sample Evaluation (Ok / Not Ok)</h4>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 border">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Sample No</th>
                                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Defect Category</th>
                                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Defect Count</th>
                                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Upload Image</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 text-center">
                            {samples.map((sample, idx) => {
                                const isNotOk = sample.status === 'Not Okay';
                                return (
                                    <tr key={idx}>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                            Sample {idx + 1}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                            <select 
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm p-2 border"
                                                value={sample.status}
                                                onChange={e => handleSampleChange(idx, 'status', e.target.value)}
                                            >
                                                <option value="Okay">Okay</option>
                                                <option value="Not Okay">Not Okay</option>
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                            <select 
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm p-2 border disabled:bg-gray-100 disabled:opacity-50"
                                                value={sample.defectCategory}
                                                onChange={e => handleSampleChange(idx, 'defectCategory', e.target.value)}
                                                disabled={!isNotOk}
                                            >
                                                <option value="">Select Defect</option>
                                                {DEFECT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                            <input 
                                                type="number" 
                                                min="1"
                                                placeholder="Count"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm p-2 border disabled:bg-gray-100 disabled:opacity-50"
                                                value={sample.defectCount}
                                                onChange={e => handleSampleChange(idx, 'defectCount', e.target.value)}
                                                disabled={!isNotOk}
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                            <input 
                                                type="file" 
                                                accept="image/*"
                                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
                                                onChange={e => handleSampleChange(idx, 'file', e.target.files ? e.target.files[0] : null)}
                                                disabled={!isNotOk}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CodeVerification;
